import { api } from '../api/client'
import type { Neighbourhood, NeighbourhoodHouse } from '../api/types'

/** The world's code, its own chunk: the game draws it with `lazy`, and a head start asks for it sooner. */
export const loadNeighbourhoodView = () => import('../stageville/neighbourhood/NeighbourhoodView')

/** The ground's first tiles, which the world stands its first village on, then the page going idle once it stands. */
const loadGroundTiles = () => import('../stageville/scene/ground').then((ground) => ground.afterGroundTiles())

/**
 * What the game asks for before it can draw, begun the moment the viewer is known to be signed in: the
 * neighbourhoods, then every village's houses, and the world's code, all coming in while the game's own code does
 * rather than one after another once it has, then the ground's first tiles. Each village's houses are kept as last
 * answered, so a visit opens at once on them while it asks again.
 */
export class HeadStart {
  private neighbourhoodsAsked: Promise<Neighbourhood[]> | null = null
  private readonly answered = new Map<string, NeighbourhoodHouse[]>()
  private readonly asking = new Map<string, Promise<NeighbourhoodHouse[]>>()
  private groundAsked: Promise<void> | null = null

  /** Asks for the neighbourhoods and every village's houses, the world's code and the ground's first tiles. */
  begin(): void {
    // A head start only: the game asks for each again as it draws, and says there when one fails.
    loadNeighbourhoodView().catch(() => undefined)
    void this.afterGroundTiles()
    this.neighbourhoods().then((found) => {
      for (const village of found) this.houses(village.id).catch(() => undefined)
    }, () => undefined)
  }

  /**
   * Settles once the ground's first tiles are in, or failed, and the page has gone idle: asked for once the world's code
   * is in, so they never slow it, and what the first village does not wait on (the residents) waits on them.
   */
  afterGroundTiles(): Promise<void> {
    this.groundAsked ??= loadNeighbourhoodView().then(loadGroundTiles).then(() => undefined, () => undefined)
    return this.groundAsked
  }

  /** The viewer's neighbourhoods, asked once and shared; an ask that failed is forgotten, so the next asks again. */
  neighbourhoods(): Promise<Neighbourhood[]> {
    this.neighbourhoodsAsked ??= api.neighbourhoods().catch((error: unknown) => {
      this.neighbourhoodsAsked = null
      throw error
    })
    return this.neighbourhoodsAsked
  }

  /** A village's houses as last answered, or asked for now when nothing has answered yet. */
  houses(villageId: string): Promise<NeighbourhoodHouse[]> {
    const last = this.answered.get(villageId)
    return last ? Promise.resolve(last) : this.askHouses(villageId)
  }

  /** A village's houses as last answered, or null before any answer. */
  lastHouses(villageId: string): NeighbourhoodHouse[] | null {
    return this.answered.get(villageId) ?? null
  }

  /** Asks for a village's houses again, sharing an ask already under way; a failed ask keeps the last answer. */
  askHouses(villageId: string): Promise<NeighbourhoodHouse[]> {
    const underWay = this.asking.get(villageId)
    if (underWay) return underWay
    const asked = api.houses(villageId).then((found) => {
      this.answered.set(villageId, found)
      return found
    }).finally(() => this.asking.delete(villageId))
    this.asking.set(villageId, asked)
    return asked
  }
}
